/**
 * Constants for LLM response generation
 * Centralized configuration values for the userResponse module
 */

// ============================================
// Retry & Attempt Limits
// ============================================

/** Maximum attempts for query validation with LLM fix */
export const MAX_QUERY_VALIDATION_RETRIES = 3;

/** Maximum attempts for query execution in text response generation */
export const MAX_QUERY_ATTEMPTS = 6;

/** Maximum attempts for external tool execution */
export const MAX_TOOL_ATTEMPTS = 3;

// ============================================
// Streaming Configuration
// ============================================

/** Flush interval for stream buffer in milliseconds */
export const STREAM_FLUSH_INTERVAL_MS = 50;

/** Heartbeat interval for progress updates during long operations */
export const PROGRESS_HEARTBEAT_INTERVAL_MS = 800;

/** Small delay for smoother streaming UX */
export const STREAM_DELAY_MS = 50;

/** Chunk size threshold for immediate flush (characters) */
export const STREAM_IMMEDIATE_FLUSH_THRESHOLD = 100;

// ============================================
// LLM Token Limits
// ============================================

/** Max tokens for query fix requests */
export const MAX_TOKENS_QUERY_FIX = 2048;

/** Max tokens for component matching */
export const MAX_TOKENS_COMPONENT_MATCHING = 8192;

/** Max tokens for category classification */
export const MAX_TOKENS_CLASSIFICATION = 1500;

/** Max tokens for UI block parameter adaptation */
export const MAX_TOKENS_ADAPTATION = 8192;

/** Max tokens for text response generation */
export const MAX_TOKENS_TEXT_RESPONSE = 4000;

/** Max tokens for next questions generation */
export const MAX_TOKENS_NEXT_QUESTIONS = 1200;

// ============================================
// Canonical row limits — ONE source of truth, by PIPELINE STAGE.
//
// The stages legitimately differ, so this is NOT a single number:
//   • FETCH  — pulled from the DB for the agent to reason over. Summarized for
//              the LLM and NEVER rendered, so it can be large.
//   • TO LLM — anything that enters an LLM prompt. Must stay tiny (token cost).
//   • RENDER — cached and drawn in a UI component. No token cost, but bounds
//              memory + render size; this also caps what the data cache holds.
// Every other row-limit name below is an ALIAS onto one of these three, so the
// whole pipeline stays consistent from one place.
// ============================================

/** STAGE 1 — FETCH: max rows an exploration/source query pulls from the DB.
 *  A raw sample is a SAMPLE at any size — accuracy comes from the agent
 *  AGGREGATING (GROUP BY), not from fetching more biased rows. Kept small; when a
 *  query hits this cap the result is flagged to the LLM as a partial, ordered
 *  sample so it aggregates instead of concluding from it. */
export const MAX_ROWS_FETCHED = 50;

/** STAGE 2 — TO LLM: largest result shown to an LLM VERBATIM (complete). Above
 *  this the LLM receives a bounded summary + a few samples instead of the rows.
 *  Small lookups still arrive complete; large ones are resolved via SQL JOINs,
 *  so the LLM never needs to eyeball them. */
export const MAX_ROWS_TO_LLM = 10;

/** STAGE 2 — small sample inlined for LLM context / stream preview / comp-gen. */
export const LLM_SAMPLE_ROWS = 10;

/** STAGE 3 — RENDER: max rows cached and drawn in a UI component. The script's
 *  final dataset and every component query are capped to this before caching. */
export const MAX_ROWS_RENDERED = 250;

// ---- Field-level (character) truncation ----
export const DEFAULT_MAX_CHARS_PER_FIELD = 500;
export const STREAM_PREVIEW_MAX_CHARS = 200;
export const TOOL_TRACKING_MAX_CHARS = 200;

// ---- Back-compat aliases → mapped onto the canonical stage limits above ----
/** @deprecated use LLM_SAMPLE_ROWS */
export const DEFAULT_MAX_ROWS_FOR_LLM = LLM_SAMPLE_ROWS;
/** Rows in a live `<DataTable>` stream preview (sample-sized). */
export const STREAM_PREVIEW_MAX_ROWS = LLM_SAMPLE_ROWS;
/** Sample rows attached to `_sampleData` (shown to the component-generator LLM). */
export const TOOL_TRACKING_SAMPLE_ROWS = LLM_SAMPLE_ROWS;
/** Tighter sample for executed-tool / component-matching context. */
export const TOOL_TRACKING_MAX_ROWS = 5;

/** Default row limit when a query specifies none (supports multi-series charts). */
export const DEFAULT_QUERY_LIMIT = 24;
/** External-tool/agent-flow query cap — result goes back to the LLM, so tiny. */
export const MAX_AGENT_QUERY_LIMIT = LLM_SAMPLE_ROWS;
/** Dashboard/report component query cap — rendered in UI, no LLM token cost. */
export const MAX_COMPONENT_QUERY_LIMIT = MAX_ROWS_RENDERED;

// ============================================
// Similarity Thresholds
// ============================================

/** Threshold for exact match in conversation search (99%) */
export const EXACT_MATCH_SIMILARITY_THRESHOLD = 0.99;

/** Default conversation similarity threshold */
export const DEFAULT_CONVERSATION_SIMILARITY_THRESHOLD = 0.8;

// ============================================
// Tool Calling Configuration
// ============================================

/** Max iterations for LLM tool calling loop */
export const MAX_TOOL_CALLING_ITERATIONS = 20;

// ============================================
// Knowledge Base Configuration
// ============================================

/** Default top K results for knowledge base queries */
export const KNOWLEDGE_BASE_TOP_K = 3;
